import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Flower2, Sparkles, Landmark, Star } from "lucide-react";
import { Reveal, Overline, SectionHeading } from "@/components/Primitives";
import { CTABand } from "@/components/Sections";
import { IMG, TRUST, SIGNATURE, FEATURED_STAYS, HERO_SLIDES, ITINERARIES, TESTIMONIALS } from "@/data/site";
import { CURRENCIES, useCurrency } from "@/hooks/useCurrency";

const ICONS = { MapPin, Flower2, Sparkles, Landmark };

export default function Home() {
  const [slide, setSlide] = useState(0);
  const { currency, setCurrency, ratesReady, format } = useCurrency();

  useEffect(() => {
    const timer = window.setInterval(() => setSlide((current) => (current + 1) % HERO_SLIDES.length), 5200);
    return () => window.clearInterval(timer);
  }, []);

  const moveSlide = (direction) => {
    setSlide((current) => (current + direction + HERO_SLIDES.length) % HERO_SLIDES.length);
  };

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-navy">
          <AnimatePresence mode="sync">
            <motion.img
              key={HERO_SLIDES[slide].img}
              src={HERO_SLIDES[slide].img}
              alt={`${HERO_SLIDES[slide].title}, ${HERO_SLIDES[slide].location}`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ opacity: { duration: 0.9 }, scale: { duration: 5.2 } }}
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-navy/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-navy/20" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto w-full min-h-[100svh] px-5 md:px-10 pt-28 pb-32 flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl [text-shadow:0_2px_30px_rgba(10,25,47,0.5)]"
          >
            <Overline className="text-gold">Discover India · Authentic Journeys</Overline>
            <h1 className="mt-5 font-serif font-light text-4xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ivory">
              Discover India,<br />the Authentic Way
            </h1>
            <p className="mt-6 sm:mt-7 max-w-2xl text-base sm:text-lg md:text-xl font-light leading-relaxed text-ivory/85">
              Curated Ayurveda, yoga, beaches, mountains, houseboats and traditional living
              experiences — timeless journeys, handcrafted for the curious traveller.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-partner-cta"
                className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full bg-gold text-forest px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-1"
              >
                Plan Your Journey <ArrowRight size={16} />
              </Link>
              <Link
                to="/experiences"
                data-testid="hero-explore-cta"
                className="w-full sm:w-auto justify-center inline-flex items-center gap-2 rounded-full border border-ivory/50 text-ivory px-6 sm:px-8 py-4 text-sm font-semibold tracking-wide transition-colors hover:bg-ivory hover:text-forest"
              >
                Explore Journeys
              </Link>
            </div>
          </motion.div>
          <div className="absolute left-5 right-20 sm:right-40 md:left-10 bottom-7 flex items-end justify-between gap-3">
            <div className="text-ivory [text-shadow:0_2px_16px_rgba(10,25,47,0.8)]">
              <p className="overline text-gold">{String(slide + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}</p>
              <p className="mt-1 text-sm font-semibold">{HERO_SLIDES[slide].title}</p>
              <p className="text-xs text-ivory/70">{HERO_SLIDES[slide].location}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" onClick={() => moveSlide(-1)} aria-label="Previous destination" className="w-11 h-11 rounded-full border border-ivory/50 text-ivory grid place-items-center hover:bg-ivory hover:text-forest transition-colors"><ArrowLeft size={18} /></button>
              <button type="button" onClick={() => moveSlide(1)} aria-label="Next destination" className="w-11 h-11 rounded-full border border-ivory/50 text-ivory grid place-items-center hover:bg-ivory hover:text-forest transition-colors"><ArrowRight size={18} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-ivory py-24 md:py-32" data-testid="trust-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <SectionHeading
              overline="Why Travel Bug India"
              title="Journeys designed to be felt, not rushed"
              subtitle="We craft unhurried, deeply personal journeys across India — with warm local hosts, boutique stays and authentic experiences that leave you genuinely rested."
            />
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TRUST.map((t, i) => {
              const Icon = ICONS[t.icon];
              return (
                <Reveal key={t.title} delay={i * 0.08}>
                  <div className="h-full rounded-2xl border border-sand bg-coconut p-8 transition-shadow duration-300 hover:shadow-[0_20px_60px_-30px_rgba(26,54,38,0.4)]" data-testid={`trust-card-${i}`}>
                    <div className="w-12 h-12 rounded-full bg-forest/8 flex items-center justify-center text-teal">
                      <Icon size={22} />
                    </div>
                    <h3 className="mt-6 font-serif text-2xl text-forest">{t.title}</h3>
                    <p className="mt-3 text-sm text-navy/65 font-light leading-relaxed">{t.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY SOUTH INDIA - overlapping split */}
      <section className="bg-sand/30 py-24 md:py-32 overflow-hidden" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <Reveal>
            <Overline className="text-gold">Why India</Overline>
            <h2 className="mt-4 font-serif font-normal text-4xl md:text-5xl leading-[1.05] tracking-tight text-forest">
              A rare balance of healing, coastline & slow living
            </h2>
            <p className="mt-6 text-lg font-light leading-relaxed text-navy/70">
              India offers a rare combination of healing traditions, coastline, forests,
              mountains, backwaters, boutique hospitality, and slower rhythms of living. It is
              ideal for travellers looking for relaxation, wellness, culture, and nature
              without the rush of mainstream tourism.
            </p>
            <ul className="mt-8 space-y-3">
              {["Ayurveda & wellness traditions", "Backwaters, beaches & the Western Ghats", "Boutique heritage stays & authentic cuisine", "Unhurried, relaxation-led travel"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-navy/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/destinations" className="mt-10 inline-flex items-center gap-2 text-forest font-semibold text-sm hover:gap-3 transition-all" data-testid="why-destinations-link">
              Explore Destinations <ArrowRight size={16} />
            </Link>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative h-[380px] sm:h-[520px]">
              <div className="absolute top-0 right-0 w-[72%] h-[62%] rounded-2xl overflow-hidden">
                <img src={IMG.backwatersSunset} alt="Backwaters" className="w-full h-full object-cover" />
              </div>
              <div className="absolute bottom-0 left-0 w-[58%] h-[58%] rounded-2xl overflow-hidden border-8 border-sand/30">
                <img src={FEATURED_STAYS[11].img} alt="South Indian heritage stay" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 right-6 bg-forest text-ivory rounded-xl px-6 py-5 hidden sm:block">
                <p className="font-serif text-4xl leading-none">13+</p>
                <p className="overline text-ivory/60 mt-1">Signature destinations</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SIGNATURE EXPERIENCES - bento */}
      <section className="bg-ivory py-24 md:py-32" data-testid="signature-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <SectionHeading overline="Signature Experiences" title="Curated ways to experience India" />
          </Reveal>
          <div className="mt-14 grid lg:grid-cols-4 auto-rows-[240px] gap-5">
            {SIGNATURE.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06} className={s.span}>
                <Link to="/experiences" data-testid={`signature-card-${i}`} className={`group relative block h-full w-full overflow-hidden rounded-2xl`}>
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/10 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <h3 className="font-serif text-2xl text-ivory">{s.title}</h3>
                    <p className="mt-1 text-sm text-ivory/75 font-light">{s.desc}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HANDPICKED STAYS */}
      <section className="bg-sand/30 py-24 md:py-32" data-testid="featured-stays-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <SectionHeading
                overline="Handpicked South India Stays"
                title="Resorts with a true sense of place"
                subtitle="From Kerala's backwaters and heritage coast to Karnataka's forests and Tamil Nadu's temple country."
              />
              <Link to="/experiences#stays" className="inline-flex items-center gap-2 text-forest font-semibold text-sm hover:gap-3 transition-all">
                View the collection <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURED_STAYS.slice(0, 6).map((stay, i) => (
              <Reveal key={stay.name} delay={(i % 3) * 0.07}>
                <Link to="/contact" className="group relative block h-[340px] overflow-hidden rounded-2xl" data-testid={`featured-stay-${i}`}>
                  <img src={stay.img} alt={stay.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/15 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <p className="overline text-gold">{stay.location}</p>
                    <h3 className="mt-2 font-serif text-2xl text-ivory">{stay.name}</h3>
                    <p className="mt-1 text-sm text-ivory/70">{stay.style}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* B2B block */}
      <section className="grain-dark relative bg-forest text-ivory overflow-hidden" data-testid="b2b-section">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden h-[320px] sm:h-[440px]">
              <img src={FEATURED_STAYS[9].img} alt="Coconut Lagoon backwater resort" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline className="text-gold">The Travel Bug Way</Overline>
            <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl leading-[1.03] tracking-tight">
              Every journey, thoughtfully handcrafted
            </h2>
            <p className="mt-6 text-lg font-light text-ivory/75 leading-relaxed">
              We design each journey by hand — with curated stays, warm local hosts, wellness
              experiences, private transfers and seamless planning — so all you have to do is
              slow down and feel India.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {["Handpicked boutique stays", "Bespoke itinerary design", "Private, easy transfers", "Wellness & Ayurveda access", "Signature experiences", "Local hosts & support"].map((x) => (
                <div key={x} className="flex items-center gap-3 text-ivory/85 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {x}
                </div>
              ))}
            </div>
            <Link to="/partnership" data-testid="b2b-discuss-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold text-forest px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-1">
              Discover Our Approach <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SAMPLE JOURNEYS */}
      <section className="bg-ivory py-24 md:py-32" data-testid="journeys-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <SectionHeading overline="Sample Journeys" title="Premium routes, ready to tailor" />
              <Link to="/itineraries" className="inline-flex items-center gap-2 text-forest font-semibold text-sm hover:gap-3 transition-all" data-testid="journeys-view-all">
                View all itineraries <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ITINERARIES.slice(0, 3).map((it, i) => (
              <Reveal key={it.title} delay={i * 0.08}>
                <div className="group h-full rounded-2xl border border-sand bg-coconut overflow-hidden" data-testid={`journey-card-${i}`}>
                  <div className="h-52 overflow-hidden">
                    <img src={it.img} alt={it.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-5 sm:p-7">
                    <div className="flex flex-wrap gap-2">
                      {it.badges.map((b) => (
                        <span key={b} className="rounded-full border border-gold text-gold text-[0.65rem] tracking-widest uppercase px-3 py-1">{b}</span>
                      ))}
                    </div>
                    <h3 className="mt-4 font-serif text-2xl text-forest">{it.title}</h3>
                    <p className="mt-2 text-sm text-navy/60 font-light">{it.days} · {it.places}</p>
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                      <div>
                        <p className="font-semibold text-forest">From {format(it.priceUsd)}</p>
                        {currency !== "USD" && <p className="text-[0.68rem] text-navy/45">USD reference: ${it.priceUsd.toLocaleString("en-US")}</p>}
                      </div>
                      <label className="text-[0.68rem] text-navy/45">
                        <span className="sr-only">Currency for {it.title}</span>
                        <select aria-label={`Currency for ${it.title}`} value={currency} onChange={(event) => setCurrency(event.target.value)} className="border border-sand bg-ivory rounded px-2 py-1 font-semibold text-forest outline-none focus:border-gold">
                          {CURRENCIES.map((item) => <option key={item} disabled={item !== "USD" && !ratesReady}>{item}</option>)}
                        </select>
                      </label>
                    </div>
                    <Link to="/itineraries" className="mt-5 inline-flex items-center gap-2 text-teal font-semibold text-sm hover:gap-3 transition-all">
                      Request Full Itinerary <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-sand/30 py-24 md:py-32" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><SectionHeading overline="Traveller stories" title="What our travellers say" align="center" className="mx-auto" /></Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.1}>
                <figure className="h-full rounded-2xl border border-sand bg-ivory p-8" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-1 text-gold" aria-label="5 out of 5 stars">{Array.from({ length: 5 }).map((_, star) => <Star key={star} size={15} fill="currentColor" />)}</div>
                  <div className="font-serif text-5xl text-gold leading-none">“</div>
                  <blockquote className="mt-2 text-navy/80 font-light leading-relaxed">{t.quote}</blockquote>
                  <figcaption className="mt-6">
                    <p className="font-semibold text-forest">{t.name}</p>
                    <p className="text-sm text-navy/55">{t.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
